import React, { useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { type Template } from 'src/types/template.types';
import { type TemplateSelectProps } from 'src/components/campaigns/forms/TemplateSelectUtils';

export interface GenerateAllOrderingsCheckboxProps {
  control: TemplateSelectProps['control'];
  index: number;
  name: string;
  isFirstMultiTitleTemplate: boolean;
  parentFieldName: string;
  templates: Template[];
}

export const GenerateAllOrderingsCheckbox: React.FC<GenerateAllOrderingsCheckboxProps> = ({
  control,
  index,
  name,
  isFirstMultiTitleTemplate,
  parentFieldName,
  templates,
}) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const { field } = useController({
    control,
    name,
  });
  const { onChange } = field;

  const handleChange = useCallback((checked: boolean) => {
    onChange(checked);
    // If this is the first multi-title template, apply to all others by default
    if (isFirstMultiTitleTemplate) {
      const allTemplates = getValues(parentFieldName) || [];
      allTemplates.forEach((template: { templateId?: string }, templateIndex: number) => {
        if (templateIndex !== index && template?.templateId) {
          const templateData = templates.find(t => t.id === template.templateId);
          if (templateData?.metadata?.groupSize) {
            setValue(`${parentFieldName}.${templateIndex}.generateAllOrderings`, checked);
          }
        }
      });
    }
  }, [onChange, isFirstMultiTitleTemplate, getValues, setValue, parentFieldName, index, templates]);

  return (
    <Column spacing="100">
      <Checkbox
        checked={!!field.value}
        data-testid={`generate-all-orderings-${index}`}
        onChange={handleChange}
      >
        {t('form_generate_all_orderings')}
      </Checkbox>
      {isFirstMultiTitleTemplate && (
        <Text color="secondary" type="b200">
          {t('form_generate_all_orderings_help')}
        </Text>
      )}
    </Column>
  );
};
